import { t } from '@/features/i18n/utils';
import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { noop, useInfiniteQuery } from '@tanstack/react-query';
import z from 'zod';
import { Add01Icon, SquareCheckBigIcon } from '@hugeicons-pro/core-stroke-rounded';
import { useAuth } from '@/features/auth';
import { projectsLiteQuery } from '@/features/projects/queries';
import { tasksQuery } from '@/features/tasks/queries';
import { createTaskAtom, tasksDisplayAtom, tasksFilterAtom } from '@/features/tasks/store';
import { MY_TASKS_TABS } from '@/features/tasks/constants';
import { isDueByToday, myTaskListFilters, myTaskListStatuses } from '@/features/tasks/utils';
import { CreateTask } from '@/features/tasks/components/create-task';
import { TaskBoard } from '@/features/tasks/components/task-board';
import { TaskError } from '@/features/tasks/components/task-error';
import { TaskTable } from '@/features/tasks/components/task-table';
import { TaskDetailPanel } from '@/features/tasks/components/task-panel';
import { TasksDisplayMenu } from '@/features/tasks/components/tasks-display-menu';
import { TasksFilterMenu } from '@/features/tasks/components/tasks-filter';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { Header, HeaderOrganizationMenu } from '@/ui/components/header';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Icon } from '@/ui/components/icon';
import {
  MainActions,
  MainActionsEnd,
  MainActionsSearchInput,
  MainHeader,
  MainScrollArea,
} from '@/ui/components/main';
import { H1 } from '@/ui/components/heading';
import { Button } from '@/ui/components/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import { Spinner } from '@/ui/components/spinner';
import { Tabs, TabsList, TabsTab } from '@/ui/components/tabs';
import { ResizablePanel, ResizablePanelGroup } from '@/ui/components/resizable';
import { useDebounce } from '@/ui/hooks/use-debounce';
import { useInfiniteScroll } from '@/ui/hooks/use-infinite-scroll';

export const Route = createFileRoute('/_authed/_organization/my-tasks')({
  component: RouteComponent,
  validateSearch: z.object({
    taskId: z.uuid().optional().catch(undefined),
    tab: z.enum(MY_TASKS_TABS).catch('today'),
  }),
  loaderDeps: ({ search }) => ({ tab: search.tab }),
  loader: async ({ context, deps }) => {
    const store = getDefaultStore();
    const filters = myTaskListFilters({
      user: context.auth.user,
      tab: deps.tab,
      filter: store.get(tasksFilterAtom),
      display: store.get(tasksDisplayAtom),
      search: '',
    });
    if (filters) context.queryClient.infiniteQuery(tasksQuery(filters)).catch(noop);
    await context.queryClient.query({
      ...projectsLiteQuery(context.auth.user.organizationId),
      staleTime: 'static',
    });
  },
});

function RouteComponent() {
  const auth = useAuth();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const setCreateTask = useSetAtom(createTaskAtom);
  const filter = useAtomValue(tasksFilterAtom);
  const display = useAtomValue(tasksDisplayAtom);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const filters = myTaskListFilters({
    user: auth.user,
    tab,
    filter,
    display,
    search: debouncedSearch,
  });
  const query = useInfiniteQuery({
    ...tasksQuery(filters ?? { organizationId: auth.user.organizationId }),
    enabled: !!filters,
  });
  const tasks = useMemo(() => {
    if (!filters) return [];
    const items = query.data?.pages.flatMap((page) => page.data) ?? [];
    return tab === 'today' ? items.filter(isDueByToday) : items;
  }, [filters, query.data, tab]);
  const scrollRef = useInfiniteScroll<HTMLDivElement>({
    onLoadMore: query.fetchNextPage,
    hasMore:
      query.hasNextPage &&
      (tab !== 'today' || !!query.data?.pages.at(-1)?.data.every(isDueByToday)),
    isLoading: query.isFetchingNextPage,
  });
  return (
    <>
      <Header>
        <HeaderOrganizationMenu />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={SquareCheckBigIcon} />
            {t({ en: 'My tasks', nb: 'Mine oppgaver' })}
          </BreadcrumbPage>
        </Breadcrumb>
      </Header>
      <ResizablePanelGroup>
        <ResizablePanel minSize="320px" className="flex min-w-0 flex-col">
          <MainHeader>
            <H1>{t({ en: 'My tasks', nb: 'Mine oppgaver' })}</H1>
            <Button
              className="max-md:w-full"
              kind="with-icon"
              onClick={() => setCreateTask({ open: true, status: 'open' })}
            >
              <Icon icon={Add01Icon} />
              {t({ en: 'New task', nb: 'Ny oppgave' })}
            </Button>
          </MainHeader>
          <Tabs
            value={tab}
            onValueChange={(tab) =>
              navigate({ to: '.', replace: true, search: (prev) => ({ ...prev, tab }) })
            }
          >
            <TabsList className="px-x">
              <TabsTab value="today">
                {t({ en: 'Today & overdue', nb: 'I dag og forfalt' })}
              </TabsTab>
              <TabsTab value="assigned">{t({ en: 'Assigned to me', nb: 'Tildelt meg' })}</TabsTab>
              <TabsTab value="created">
                {t({ en: 'Created by me', nb: 'Opprettet av meg' })}
              </TabsTab>
            </TabsList>
          </Tabs>
          <MainActions className="mb-0 border-t-0!">
            <TasksFilterMenu />
            <MainActionsEnd>
              <MainActionsSearchInput
                aria-label={t({ en: 'Search tasks', nb: 'Søk i oppgaver' })}
                placeholder={t({ en: 'Search tasks', nb: 'Søk i oppgaver' })}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <TasksDisplayMenu />
            </MainActionsEnd>
          </MainActions>
          <MainScrollArea viewportRef={scrollRef}>
            <div className="flex min-h-full shrink-0 flex-col pb-3">
              {(query.isPending || query.isPlaceholderData) && filters ? (
                <Spinner kind="inset" />
              ) : query.isError ? (
                <div className="p-4">
                  <TaskError error={query.error} onRetry={() => query.refetch()} />
                </div>
              ) : display.view === 'list' && (tasks.length || tab === 'today') ? (
                <TaskTable
                  key={JSON.stringify(filters)}
                  tasks={tasks}
                  loading={query.isFetchingNextPage}
                  groupBy={tab === 'today' ? 'due' : 'status'}
                />
              ) : display.view === 'board' && tasks.length ? (
                <TaskBoard
                  tasks={tasks}
                  statuses={myTaskListStatuses(filter, tab)}
                  loading={query.isFetchingNextPage}
                />
              ) : (
                <Empty>
                  <EmptyMedia variant="icon">
                    <Icon icon={SquareCheckBigIcon} />
                  </EmptyMedia>
                  <EmptyContent>
                    <EmptyTitle>{t({ en: 'My tasks', nb: 'Mine oppgaver' })}</EmptyTitle>
                    <EmptyDescription>
                      {t({
                        en: 'Tasks assigned to or created by you will show here.',
                        nb: 'Oppgaver som er tildelt deg eller opprettet av deg vises her.',
                      })}
                    </EmptyDescription>
                  </EmptyContent>
                </Empty>
              )}
            </div>
          </MainScrollArea>
        </ResizablePanel>
        <TaskDetailPanel />
      </ResizablePanelGroup>
      <SuspenseBoundary>
        <CreateTask />
      </SuspenseBoundary>
    </>
  );
}
